import React, { useEffect, useState } from 'react';
import { contract, chainNow } from '../lib';

// Pool-open notice. Everything is read live from Genesis: openPool() is callable by anyone once
// requested >= lpTrigger (sellout) or block.timestamp >= mintEnd; LP tokens are minted to 0xdEaD.
const pad = (x) => String(x).padStart(2, '0');
function fmtLeft(s) {
  if (s <= 0) return '0s';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
  return `${d ? d + 'd ' : ''}${pad(h)}h ${pad(m)}m ${pad(x)}s`;
}

export default function LpBanner({ nonce }) {
  const [st, setSt] = useState(null);
  const [skew, setSkew] = useState(0); // chain time - local time
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    let alive = true;
    const g = contract('Genesis');
    const load = async () => {
      const [mintEnd, poolOpened, requested, lpTrigger] = await Promise.all([g.mintEnd(), g.poolOpened(), g.requested(), g.lpTrigger()]);
      if (alive) setSt({ mintEnd: Number(mintEnd), poolOpened: Boolean(poolOpened), requested: Number(requested), lpTrigger: Number(lpTrigger) });
    };
    chainNow().then((c) => alive && setSkew(Number(c) - Math.floor(Date.now() / 1000))).catch(() => {});
    load().catch(() => {});
    const iv = setInterval(() => load().catch(() => {}), 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [nonce]);
  useEffect(() => { const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000); return () => clearInterval(t); }, []);
  if (!st) return null;
  const t = now + skew;
  const soldOut = st.requested >= st.lpTrigger;
  const when = new Date(st.mintEnd * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  let head, sub;
  if (st.poolOpened) { head = 'Liquidity pool is live'; sub = 'ORE/BNB pool seeded on PancakeSwap. LP tokens sent to 0xdEaD.'; }
  else if (soldOut || t >= st.mintEnd) { head = soldOut ? 'Sold out — liquidity pool opens now' : 'Mint closed — liquidity pool opens now'; sub = 'Anyone can call openPool() on the Genesis contract. LP tokens go to 0xdEaD.'; }
  else { head = 'Liquidity pool opens when the mint ends — no matter how many ships are minted'; sub = `${when} · or earlier on sellout · LP tokens burned to 0xdEaD`; }
  const live = !st.poolOpened && !soldOut && t < st.mintEnd;
  // Critical styles inline: /App.css is unhashed and browser-cached for 4h, so never depend on it here.
  const S = {
    box: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px 16px', flexWrap: 'wrap', margin: '0 0 16px', padding: '14px 18px', background: 'linear-gradient(90deg,rgba(244,180,0,.16),rgba(0,212,255,.08))', border: '2px solid #F4B400', borderLeft: '6px solid #F4B400', boxSizing: 'border-box' },
    text: { display: 'block', flex: '1 1 300px', minWidth: 0 },
    head: { display: 'block', color: '#F4B400', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3, margin: 0 },
    sub: { display: 'block', color: '#E2E8F0', fontSize: '.88rem', opacity: 0.9, marginTop: 4 },
    clock: { display: 'block', flex: '0 0 auto' },
    lab: { display: 'block', color: '#8090B0', fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.08em' },
    cd: { display: 'block', color: '#00D4FF', fontWeight: 700, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' },
  };
  return (
    <div className="lp-banner" data-testid="lp-banner" role="note" style={S.box}>
      <div style={S.text}><strong style={S.head}>{head}</strong>{' '}<span style={S.sub}>{sub}</span></div>
      {' '}
      {live && <div style={S.clock}><span style={S.lab}>Pool opens in:</span>{' '}<b style={S.cd}>{fmtLeft(st.mintEnd - t)}</b></div>}
    </div>
  );
}
